'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useAuth } from '@clerk/nextjs'
import { normalizeStationStatusFields } from '@/utils/stationStatus'
import { usePreferences } from '@/contexts/preferences-context'
import { useGlobalMemory, useGlobalMemoryActions } from '@/contexts/global-memory-context'

const StationsContext = createContext()

export function useStations() {
  const context = useContext(StationsContext)
  if (!context) throw new Error('useStations must be used within a StationsProvider')
  return context
}

export function StationsProvider({ children }) {
  const { getToken, orgId } = useAuth()
  const { stationVisibilityScope, loading: preferencesLoading } = usePreferences()
  const catalog = useGlobalMemory((state) => state.catalog)
  const { setCatalog } = useGlobalMemoryActions()

  const fetchStations = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setCatalog({ status: 'loading', error: null })
      const token = await getToken()
      const type = stationVisibilityScope === 'owned' || stationVisibilityScope === 'commissioned'
        ? stationVisibilityScope
        : 'all'
      const apiUrl = `${process.env.NEXT_PUBLIC_BACKEND_API}/stations/all.json?simple=true&type=${type}`
      const response = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      const items = Array.isArray(data) ? data.map(normalizeStationStatusFields) : []
      setCatalog({
        items,
        status: 'ready',
        error: null,
        scope: type,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      if (!silent) setCatalog({ status: 'error', error: error.message })
      else console.error('Background station refresh failed:', error)
    }
  }, [getToken, setCatalog, stationVisibilityScope])

  useEffect(() => {
    if (preferencesLoading) return
    fetchStations()
  }, [fetchStations, orgId, preferencesLoading])

  useEffect(() => {
    if (preferencesLoading) return undefined
    const refreshInterval = setInterval(() => fetchStations({ silent: true }), 60000)
    return () => clearInterval(refreshInterval)
  }, [fetchStations, preferencesLoading])

  const value = useMemo(() => ({
    stations: catalog.items,
    loading: catalog.status === 'idle' || catalog.status === 'loading',
    error: catalog.error,
    refetch: fetchStations,
  }), [catalog.error, catalog.items, catalog.status, fetchStations])

  return <StationsContext.Provider value={value}>{children}</StationsContext.Provider>
}
